import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { niceDate, fmtNL, isoDate, addDays } from "@/lib/dates";
import { Phone, MessageCircle, MapPin, Truck, Play, Square, KeyRound, ParkingSquare, Camera, X, CalendarDays, Clock, AlertTriangle, WifiOff, Wifi, RefreshCw, Coffee } from "lucide-react";
import { toast } from "sonner";
import { clientId, queueLength, readLocalTimer, saveLocalTimer, flushQueue, isOnline } from "@/lib/offline";

// Lokale, ultra-korte cache voor de Vandaag-endpoint. Doel: wanneer een
// uitvoerder de app wegklikt en na 30s terugkomt, ziet hij direct de vorige
// planning i.p.v. een leeg scherm. Cache-per-datum, TTL 5 minuten.
const VANDAAG_CACHE_TTL_MS = 5 * 60 * 1000;
function _cacheKey(userId, dateIso) { return `pb_vandaag_v2_${userId || "anon"}_${dateIso}`; }
function readVandaagCache(userId, dateIso) {
  try {
    const raw = localStorage.getItem(_cacheKey(userId, dateIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed.at || 0) > VANDAAG_CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}
function writeVandaagCache(userId, dateIso, data) {
  try { localStorage.setItem(_cacheKey(userId, dateIso), JSON.stringify({ at: Date.now(), data })); }
  catch { /* private mode */ }
}

function buildAddressQuery(p) {
  if (!p) return "";
  const street = (p.address || "").trim();
  const hn = (p.house_number || "").trim();
  const pc = (p.postcode || "").trim();
  const city = (p.city || "").trim();
  const streetLine = hn && !new RegExp(`\\b${hn.replace(/[.*+?^${}()|[\\]\\\\]/g,"\\$&")}\\b\\s*$`).test(street) ? `${street} ${hn}` : street;
  return [streetLine, pc, city].filter(Boolean).join(", ");
}

function formatElapsed(startIso) {
  if (!startIso) return "00:00:00";
  const start = new Date(startIso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - start) / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export default function Vandaag() {
  const { user } = useAuth();
  const todayIso = isoDate(new Date());
  const [viewDate, setViewDate] = useState(todayIso);
  const [dayAssignments, setDayAssignments] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [tick, setTick] = useState(0);
  const [mySummary, setMySummary] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [pendingSync, setPendingSync] = useState(queueLength());
  const [fromCache, setFromCache] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const reminderShownRef = useRef(false);

  const dayOptions = Array.from({ length: 15 }, (_, i) => {
    const d = addDays(new Date(), i);
    const iso = isoDate(d);
    let label;
    if (i === 0) label = `Vandaag · ${fmtNL(d)}`;
    else if (i === 1) label = `Morgen · ${fmtNL(d)}`;
    else label = niceDate(d);
    return { value: iso, label };
  });

  // Load-first-from-cache pattern (SWR): render meteen wat we hebben, verniewen
  // op de achtergrond. Voorheen bleef Vandaag ~2-5s op wit staan bij trage 3G
  // omdat alle data uit netwerk moest komen.
  const load = useCallback(async () => {
    const cached = readVandaagCache(user?.id, viewDate);
    if (cached && cached.data) {
      setDayAssignments(cached.data);
      setFromCache(true);
      setInitialLoading(false);
    }
    try {
      const t = await api.get("/vandaag", { params: { target_date: viewDate } });
      setDayAssignments(t.data);
      setFromCache(!!t._fromCache);
      writeVandaagCache(user?.id, viewDate, t.data);
    } catch (e) {
      if (!cached) {
        if (!isOnline()) toast.warning("Offline — laatste opgeslagen planning wordt gebruikt zodra beschikbaar.");
        else toast.error("Kon planning niet laden");
      }
    } finally {
      setInitialLoading(false);
    }
  }, [viewDate, user?.id]);

  useEffect(() => { setInitialLoading(true); load(); }, [load]);

  const loadTimer = useCallback(async () => {
    try {
      const r = await api.get("/time-entries/active");
      setActiveTimer(r.data || null);
      if (r.data) saveLocalTimer(r.data); else saveLocalTimer(null);
    } catch {
      // Offline fallback → gebruik lokale timer state indien beschikbaar
      const local = readLocalTimer();
      if (local) setActiveTimer(local);
    }
  }, []);

  const loadMySummary = useCallback(async () => {
    try {
      // Eén backend-aggregation vervangt de vroegere drie parallelle /time-entries
      // calls. Backend berekent week/maand/totaal met $group in Mongo — bij grote
      // klanten (>1000 entries) is dit ~30× sneller en veel lichter voor het netwerk.
      const r = await api.get("/time-entries/my-summary");
      const d = r.data || {};
      setMySummary({
        week: d.week_minutes || 0,
        month: d.month_minutes || 0,
        total: d.total_minutes || 0,
      });
    } catch (err) { console.warn("Urenoverzicht laden mislukt", err); }
  }, []);

  useEffect(() => { loadTimer(); loadMySummary(); }, [loadTimer, loadMySummary]);

  // Timer tick every second when active
  useEffect(() => {
    if (!activeTimer) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [activeTimer]);

  // Poll active timer every 30s (auto-stop detection)
  useEffect(() => {
    const t = setInterval(() => { loadTimer(); }, 30000);
    return () => clearInterval(t);
  }, [loadTimer]);

  // 16:00 reminder to stop timer
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const dayKey = `pb_reminder_${now.toISOString().slice(0,10)}`;
      let alreadyShown = false;
      try { alreadyShown = !!localStorage.getItem(dayKey); } catch (_) { /* private mode: skip persistence */ }
      if (activeTimer && now.getHours() >= 16 && now.getHours() < 17 && !reminderShownRef.current && !alreadyShown) {
        reminderShownRef.current = true;
        try { localStorage.setItem(dayKey, "1"); } catch (_) { /* private mode */ }
        toast.warning("Vergeet niet op STOP te klikken! Om 17:00 wordt je timer automatisch gestopt.", { duration: 15000 });
      }
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [activeTimer]);

  const startWork = async (a) => {
    const cid = clientId();
    try {
      const r = await api.post("/time-entries/start", { assignment_id: a.id, client_id: cid, client_start_at: new Date().toISOString() });
      setActiveTimer(r.data);
      saveLocalTimer(r.data);
      toast.success("Timer gestart");
      await load();
    } catch (e) {
      if (e && e.offline) {
        setActiveTimer(e.synthetic);
        setPendingSync(queueLength());
        toast.warning("Offline — timer draait lokaal en wordt gesynct zodra er weer bereik is.", { duration: 6000 });
        return;
      }
      const msg = e?.response?.data?.detail || "Kon niet starten";
      toast.error(msg);
    }
  };

  const stopWork = async (breakMinutes) => {
    const cid = activeTimer?.client_id || clientId();
    const clientTrackedBreak = activeTimer?.break_minutes || 0;
    try {
      const res = await api.post("/time-entries/stop", {
        client_id: cid,
        client_stop_at: new Date().toISOString(),
        break_minutes: breakMinutes,
        client_break_minutes: clientTrackedBreak,
      });
      // Timer kan automatisch door-starten op volgende klus van dezelfde dag.
      const next = res.data?.next_timer;
      if (next) {
        setActiveTimer(next);
        saveLocalTimer(next);
        toast.success("Timer gestopt — automatisch doorgestart op volgende klus", { duration: 6000 });
      } else {
        toast.success("Timer gestopt");
        setActiveTimer(null);
        saveLocalTimer(null);
      }
      setShowBreakModal(false);
      await loadMySummary();
      await load();
    } catch (e) {
      if (e && e.offline) {
        setActiveTimer(null);
        saveLocalTimer(null);
        setPendingSync(queueLength());
        setShowBreakModal(false);
        toast.warning("Offline — stop is opgeslagen en wordt gesynct zodra er weer bereik is.", { duration: 6000 });
        return;
      }
      toast.error("Kon niet stoppen");
    }
  };

  const togglePause = async () => {
    if (!activeTimer) return;
    const cid = activeTimer.client_id || clientId();
    const action = activeTimer.is_paused ? "stop" : "start";
    try {
      const r = await api.post("/time-entries/pause", { client_id: `${cid}-${Date.now()}`, action, client_at: new Date().toISOString() });
      // Reload real timer state van server
      const active = await api.get("/time-entries/active");
      setActiveTimer(active.data || null);
      if (active.data) saveLocalTimer(active.data);
      toast.success(action === "start" ? "Pauze gestart" : "Pauze gestopt");
    } catch (e) {
      if (e && e.offline) {
        // Lokaal bijhouden: bij pauze-start noteer paused_at; bij pauze-stop tel elapsed op bij break_minutes
        const now = new Date().toISOString();
        const cur = activeTimer;
        if (action === "start") {
          const next = { ...cur, is_paused: true, paused_at: now };
          setActiveTimer(next); saveLocalTimer(next);
          toast.warning("Offline — pauze wordt lokaal bijgehouden.");
        } else {
          const pausedSince = cur.paused_at ? new Date(cur.paused_at) : new Date();
          const addMin = Math.max(0, Math.round((Date.now() - pausedSince.getTime()) / 60000));
          const next = { ...cur, is_paused: false, paused_at: null, break_minutes: (cur.break_minutes || 0) + addMin };
          setActiveTimer(next); saveLocalTimer(next);
          toast.warning(`Offline — pauze van ${addMin} min lokaal genoteerd.`);
        }
        setPendingSync(queueLength());
        return;
      }
      toast.error("Pauze kon niet worden bijgewerkt");
    }
  };

  const askBreakThenStop = () => {
    // Achteraf B: vraag "hoeveel pauze had je?" voordat we stoppen.
    // Als er offline handmatig al pauze getikt is, wordt die opgeteld door backend (client_break_minutes).
    setShowBreakModal(true);
  };

  // Online/offline reactie: badge + queue flush.
  useEffect(() => {
    const onOnline = async () => {
      setOnline(true);
      const q = queueLength();
      if (q > 0) {
        toast.info(`Verbinding terug — ${q} actie(s) worden gesynct…`);
        const res = await flushQueue(api);
        setPendingSync(queueLength());
        if (res.flushed > 0) toast.success(`${res.flushed} actie(s) gesynct met server`);
        if (res.failed > 0) toast.error(`${res.failed} actie(s) konden niet worden verwerkt`);
      }
      // Refresh vanaf server
      await load();
      await loadTimer();
    };
    const onOffline = () => { setOnline(false); toast.warning("Verbinding verloren — je kunt gewoon doorwerken."); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [load, loadTimer]);

  const manualSync = async () => {
    const res = await flushQueue(api);
    setPendingSync(queueLength());
    if (res.flushed > 0) toast.success(`${res.flushed} actie(s) gesynct`);
    else if (res.remaining) toast.error("Nog geen verbinding");
    else toast.info("Niets te synchroniseren");
    await load();
    await loadTimer();
  };

  const isToday = viewDate === todayIso;

  return (
    <div className="max-w-3xl mx-auto p-4 pb-28 md:p-8 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Mijn planning</div>
          <h1 className="pb-heading text-3xl md:text-4xl font-black">{niceDate(new Date(viewDate))}</h1>
        </div>
        <div className="flex items-center gap-2" data-testid="offline-status">
          {!online && (
            <div className="flex items-center gap-1 rounded-full border-2 border-orange-600 bg-orange-50 text-orange-800 px-3 py-1 text-xs font-black uppercase tracking-widest" data-testid="offline-badge">
              <WifiOff size={14}/> Offline
            </div>
          )}
          {online && pendingSync === 0 && fromCache && (
            <div className="flex items-center gap-1 rounded-full border-2 border-slate-400 bg-slate-100 text-slate-700 px-3 py-1 text-[10px] font-black uppercase tracking-widest">
              <Wifi size={12}/> uit cache
            </div>
          )}
          {pendingSync > 0 && (
            <button onClick={manualSync} className="flex items-center gap-1 rounded-full border-2 border-orange-600 bg-orange-600 text-white px-3 py-1 text-xs font-black uppercase tracking-widest" data-testid="pending-sync-badge">
              <RefreshCw size={14}/> {pendingSync} te syncen
            </button>
          )}
        </div>
      </div>

      <div className="pb-card p-3" data-testid="day-picker-card">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1">
          <CalendarDays size={14}/> Kies een dag (komende 2 weken)
        </label>
        <select
          className="pb-input"
          value={viewDate}
          onChange={(e) => setViewDate(e.target.value)}
          data-testid="day-picker"
        >
          {dayOptions.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        {!isToday && (
          <button className="pb-btn-outline w-full mt-2" onClick={() => setViewDate(todayIso)} data-testid="btn-back-today">
            Terug naar vandaag
          </button>
        )}
      </div>

      {initialLoading && dayAssignments.length === 0 && (
        <div className="space-y-4" data-testid="vandaag-skeleton" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="pb-card overflow-hidden animate-pulse">
              <div className="p-5 bg-slate-800">
                <div className="h-3 w-24 bg-slate-600 rounded"/>
                <div className="mt-3 h-8 w-3/4 bg-slate-600 rounded"/>
                <div className="mt-2 h-4 w-1/2 bg-slate-700 rounded"/>
                <div className="mt-4 flex gap-2">
                  <div className="h-11 w-32 bg-slate-700 rounded"/>
                  <div className="h-11 w-20 bg-slate-700 rounded"/>
                </div>
              </div>
              <div className="p-4 border-t-2 border-slate-900 space-y-2">
                <div className="h-3 w-16 bg-slate-200 rounded"/>
                <div className="h-4 w-3/4 bg-slate-200 rounded"/>
                <div className="h-4 w-1/2 bg-slate-200 rounded"/>
              </div>
              <div className="p-4 border-t-2 border-slate-900">
                <div className="h-12 w-full bg-slate-200 rounded"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {!initialLoading && dayAssignments.length === 0 && (
        <div className="pb-card p-6 text-center">
          <div className="text-lg font-bold text-slate-700">Geen planning voor deze dag</div>
          <div className="text-sm text-slate-500 mt-1">Kies een andere dag hierboven.</div>
        </div>
      )}

      {mySummary && (
        <div className="pb-card p-4" data-testid="my-hours-summary">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Clock size={14}/> Mijn uren</div>
          <div className="grid grid-cols-3 gap-2">
            <MyHour label="Deze week" minutes={mySummary.week} testId="my-hours-week"/>
            <MyHour label="Deze maand" minutes={mySummary.month} testId="my-hours-month"/>
            <MyHour label="Totaal" minutes={mySummary.total} testId="my-hours-total"/>
          </div>
        </div>
      )}

      {dayAssignments.map((a) => {
        // Backend levert nu project/vehicle/team inline mee — geen aparte lookups meer nodig.
        // Fallback op lege objecten voor stale-cache van vóór deze update.
        const p = a.project || null;
        const v = a.vehicle || null;
        const team = a.team || (a.members || []);
        const me = team.find((m) => m.user_id === user.id) || (a.members || []).find((m) => m.user_id === user.id);
        const isDriver = me?.role_today === "chauffeur";
        const others = team.filter((m) => m.user_id !== user.id);
        return (
          <div key={a.id} className="pb-card overflow-hidden" data-testid={`vandaag-assignment-${a.id}`}>
            {/* Hero */}
            <div className="p-5 text-white" style={{ background: p?.color || "#0F172A" }}>
              <div className="text-xs uppercase tracking-widest opacity-90 font-bold">{isToday ? "Je werkt vandaag bij" : "Locatie"}</div>
              <div className="pb-heading text-3xl md:text-4xl font-black leading-tight text-white">{p?.name || "—"}</div>
              <div className="text-lg font-semibold mt-1">{buildAddressQuery(p) || `${p?.address || ""}, ${p?.city || ""}`}</div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <a
                  href={p?.latitude ? `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(buildAddressQuery(p))}`}
                  target="_blank" rel="noreferrer"
                  className="pb-btn-primary flex items-center gap-2 !border-white"
                  data-testid={`btn-navigate-${a.id}`}
                >
                  <MapPin size={20}/> NAVIGEER
                </a>
                {p?.contact_phone && (
                  <a
                    href={`tel:${p.contact_phone.replace(/[^\d+]/g, "")}`}
                    className="pb-btn-primary flex items-center gap-2 !bg-green-600 !border-white"
                    title={`Bel ${p.contact_person || p.customer_name || "klant"}`}
                    data-testid={`btn-call-customer-${a.id}`}
                  >
                    <Phone size={20}/> BEL {(p.contact_person || p.customer_name || "klant").split(" ")[0].toUpperCase()}
                  </a>
                )}
                <div className="pb-btn-outline flex items-center gap-2 !bg-white/10 !border-white !text-white">
                  <span className="pb-heading font-black text-lg">{a.start_time}</span>–<span className="pb-heading font-black text-lg">{a.end_time}</span>
                </div>
              </div>
            </div>

            {/* Photos: project + assignment (schouw) */}
            {(a.photos?.length > 0 || (p?.photos?.length > 0)) && (
              <div className="p-4 border-t-2 border-slate-900">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Camera size={14}/> Foto&apos;s van het project</div>
                <div className="flex gap-2 overflow-x-auto pb-scrollbar snap-x">
                  {[...(p?.photos || []), ...(a.photos || [])].map((src, i) => (
                    <button key={src || `photo-${i}`} onClick={() => setLightbox(src)} className="shrink-0 border-2 border-slate-900 rounded overflow-hidden snap-start" data-testid={`vandaag-photo-${a.id}-${i}`}>
                      <img src={src} alt="" loading="lazy" decoding="async" className="h-40 w-40 object-cover"/>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Team */}
            <div className="p-4 border-t-2 border-slate-900">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Ploeg</div>
              {others.length === 0 && <div className="text-sm text-slate-500">Je werkt alleen deze dag.</div>}
              <div className="space-y-2">
                {others.map((m) => {
                  // Team-lid data komt nu inline mee uit /vandaag; fallback voor oude cache.
                  const memberName = m.name || "Collega";
                  const memberPhone = m.phone || "";
                  return (
                    <div key={m.user_id} className="flex items-center justify-between border-2 border-slate-900 rounded p-2">
                      <div>
                        <div className="font-bold">{memberName}</div>
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{m.role_today}</div>
                      </div>
                      <div className="flex gap-2">
                        {memberPhone && (
                          <>
                            <a href={`tel:${memberPhone}`} className="pb-btn-outline !min-h-[44px] !px-3 flex items-center"><Phone size={18}/></a>
                            <a href={`https://wa.me/${memberPhone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="pb-btn-outline !min-h-[44px] !px-3 flex items-center"><MessageCircle size={18}/></a>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Vehicle */}
            {v && (
              <div className="p-4 border-t-2 border-slate-900 bg-slate-50">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Voertuig</div>
                <div className="flex items-center gap-3">
                  <Truck size={28} strokeWidth={2.5}/>
                  <div className="flex-1">
                    <div className="pb-heading text-xl font-black">{v.name}</div>
                    <div className="text-sm font-semibold text-slate-700">{v.license_plate}</div>
                  </div>
                  {isDriver ? (
                    <div className="pb-heading text-lg font-black text-orange-600 uppercase">Jij rijdt</div>
                  ) : (
                    <div className="text-sm font-bold text-slate-700 uppercase">Rijdt mee</div>
                  )}
                </div>
              </div>
            )}

            {a.task_description && (
              <div className="p-4 border-t-2 border-slate-900">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Taak</div>
                <div className="text-base font-semibold whitespace-pre-wrap">{a.task_description}</div>
              </div>
            )}

            {a.material_checklist?.length > 0 && (
              <div className="p-4 border-t-2 border-slate-900">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Materiaal ({isToday ? "afvinken" : "klaarleggen"})</div>
                <ul className="space-y-2">
                  {a.material_checklist.map((it, i) => (
                    <label key={`${a.id}-mat-${i}-${it}`} className="flex items-center gap-3 text-base font-semibold">
                      <input type="checkbox" className="w-6 h-6 accent-orange-600" disabled={!isToday}/> {it}
                    </label>
                  ))}
                </ul>
              </div>
            )}

            {(p?.contact_phone || p?.contact_person || p?.customer_name) && (
              <div className="p-4 border-t-2 border-slate-900 flex items-center gap-3" data-testid={`customer-contact-${a.id}`}>
                <Phone size={20} className="text-slate-700 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Klant / contact</div>
                  <div className="font-semibold truncate">{[p.contact_person, p.customer_name].filter(Boolean).join(" · ") || "—"}</div>
                  {p.contact_phone && <div className="font-black text-lg" data-testid={`customer-phone-${a.id}`}>{p.contact_phone}</div>}
                </div>
                {p.contact_phone && (
                  <div className="flex gap-2 shrink-0">
                    <a href={`tel:${p.contact_phone.replace(/[^\d+]/g, "")}`} className="pb-btn-primary !min-h-[48px] !px-4 flex items-center gap-1 !bg-green-600" data-testid={`customer-call-${a.id}`}><Phone size={18}/> Bel</a>
                    {/^(06|316|00316)/.test(p.contact_phone.replace(/[^\d]/g, "")) && (
                      <a href={`https://wa.me/${p.contact_phone.replace(/[^\d]/g, "").replace(/^00/, "").replace(/^0(?=6)/, "31")}`} target="_blank" rel="noreferrer" className="pb-btn-outline !min-h-[48px] !px-3 flex items-center" aria-label="WhatsApp klant" data-testid={`customer-whatsapp-${a.id}`}><MessageCircle size={18}/></a>
                    )}
                  </div>
                )}
              </div>
            )}

            {(p?.parking_info || p?.access_info) && (
              <div className="p-4 border-t-2 border-slate-900 bg-slate-50 space-y-2">
                {p.parking_info && (
                  <div className="flex items-start gap-2">
                    <ParkingSquare size={20} className="text-slate-700 shrink-0 mt-1"/>
                    <div><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Parkeren</div><div className="font-semibold">{p.parking_info}</div></div>
                  </div>
                )}
                {p.access_info && (
                  <div className="flex items-start gap-2">
                    <KeyRound size={20} className="text-slate-700 shrink-0 mt-1"/>
                    <div><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Toegang</div><div className="font-semibold">{p.access_info}</div></div>
                  </div>
                )}
              </div>
            )}

            {a.office_note && (
              <div className="p-4 border-t-2 border-orange-600 bg-orange-50">
                <div className="text-xs font-bold uppercase tracking-widest text-orange-700 mb-1">Notitie kantoor</div>
                <div className="font-bold">{a.office_note}</div>
              </div>
            )}

            {isToday && (
              <div className="p-4 border-t-2 border-slate-900">
                {activeTimer && activeTimer.assignment_id === a.id ? (
                  <div className="space-y-3">
                    <div className={`rounded-lg border-2 p-4 flex items-center justify-between ${activeTimer.is_paused ? "border-slate-500 bg-slate-100" : "border-orange-600 bg-orange-50"}`} data-testid={`timer-active-${a.id}`}>
                      <div>
                        <div className={`text-xs font-bold uppercase tracking-widest flex items-center gap-1 ${activeTimer.is_paused ? "text-slate-700" : "text-orange-700"}`}>
                          <Clock size={14}/> {activeTimer.is_paused ? "PAUZE — timer staat stil" : "Timer loopt"}
                        </div>
                        <div className={`pb-heading text-4xl font-black tabular-nums ${activeTimer.is_paused ? "text-slate-600" : "text-orange-700"}`} data-testid="timer-elapsed">
                          {formatElapsed(activeTimer.start_at)}
                        </div>
                        <div className="text-xs font-semibold text-slate-600 mt-1">
                          Gestart om {new Date(activeTimer.start_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                          {activeTimer.break_minutes > 0 && ` · ${activeTimer.break_minutes} min pauze`}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          className={`pb-btn-outline flex items-center gap-2 !min-h-[44px] ${activeTimer.is_paused ? "!bg-orange-600 !text-white !border-orange-800 hover:!bg-orange-700" : "!bg-white"}`}
                          onClick={togglePause}
                          data-testid={`btn-pause-${a.id}`}
                        >
                          <Coffee size={18}/> {activeTimer.is_paused ? "HERVAT" : "PAUZE"}
                        </button>
                        <button className="pb-btn-ink !bg-red-600 !border-red-800 flex items-center gap-2" onClick={askBreakThenStop} data-testid={`btn-stop-${a.id}`}>
                          <Square size={20}/> STOP
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600 font-semibold flex items-center gap-1"><AlertTriangle size={12}/> Auto-stop om 17:00 als je vergeet te stoppen.</div>
                  </div>
                ) : activeTimer && activeTimer.assignment_id !== a.id ? (
                  <div className="text-xs text-slate-600 font-semibold text-center py-2">Je hebt al een lopende timer op een ander project.</div>
                ) : (
                  <button className="pb-btn-primary w-full flex items-center justify-center gap-2" onClick={() => startWork(a)} data-testid={`btn-start-${a.id}`}>
                    <Play size={20}/> START TIMER
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-3 right-3 md:top-6 md:right-6 text-white bg-slate-900/70 rounded-full p-2 border-2 border-white z-10" onClick={() => setLightbox(null)}><X size={24}/></button>
          <img src={lightbox} alt="" className="w-full max-w-3xl max-h-[85vh] object-contain rounded"/>
        </div>
      )}

      {showBreakModal && activeTimer && (
        <BreakModal
          activeTimer={activeTimer}
          onClose={() => setShowBreakModal(false)}
          onConfirm={stopWork}
        />
      )}
    </div>
  );
}

function BreakModal({ activeTimer, onClose, onConfirm }) {
  const startDate = activeTimer?.start_at ? new Date(activeTimer.start_at) : new Date();
  const wallSec = Math.floor((Date.now() - startDate.getTime()) / 1000);
  const wallH = Math.floor(wallSec / 3600);
  const wallM = Math.floor((wallSec % 3600) / 60);
  const trackedBreak = activeTimer?.break_minutes || 0;
  // Voorstel: reeds bijgehouden pauze OF 30 als bruto > 5.5u (CAO-hint), anders 0.
  const cutoff = wallSec > 5.5 * 3600;
  const defaultBreak = trackedBreak > 0 ? trackedBreak : (cutoff ? 30 : 0);
  const [minutes, setMinutes] = React.useState(defaultBreak);
  const [saving, setSaving] = React.useState(false);
  const wallMin = Math.max(0, Math.floor(wallSec / 60));
  const net = Math.max(0, wallMin - minutes);
  const netH = Math.floor(net / 60);
  const netM = net % 60;

  const submit = async () => {
    setSaving(true);
    try { await onConfirm(minutes); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/70" onClick={onClose}>
      <div className="pb-card w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="break-modal">
        <div className="bg-orange-600 text-white p-4 flex items-center justify-between">
          <div className="pb-heading text-lg font-black flex items-center gap-2"><Coffee size={20}/> Hoeveel pauze had je?</div>
          <button onClick={onClose} className="text-white" data-testid="btn-close-break-modal"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded border-2 border-slate-900 bg-slate-50 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Vandaag gewerkt (bruto)</div>
            <div className="pb-heading text-3xl font-black tabular-nums">{wallH}u {String(wallM).padStart(2, "0")}m</div>
          </div>
          {trackedBreak > 0 && (
            <div className="text-xs text-slate-600 font-semibold">Je hebt tijdens de dag <b>{trackedBreak} min</b> pauze getikt. Klopt dat, of pas hieronder aan.</div>
          )}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Kies pauze</div>
            <div className="grid grid-cols-5 gap-2">
              {[0, 15, 30, 45, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMinutes(m)}
                  className={`h-11 rounded border-2 font-black text-sm ${minutes === m ? "border-orange-600 bg-orange-600 text-white" : "border-slate-900 bg-white hover:border-orange-600"}`}
                  data-testid={`break-preset-${m}`}
                >{m}m</button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <label className="text-xs font-black uppercase tracking-widest text-slate-500">Anders:</label>
              <input
                type="number" min="0" max="300"
                value={minutes}
                onChange={(e) => setMinutes(Math.max(0, Math.min(300, parseInt(e.target.value, 10) || 0)))}
                className="pb-input !py-2 w-24 tabular-nums text-center"
                data-testid="break-input-custom"
              />
              <span className="text-sm font-semibold text-slate-600">minuten</span>
            </div>
          </div>
          <div className="rounded border-2 border-emerald-600 bg-emerald-50 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Netto uren (na pauze)</div>
            <div className="pb-heading text-3xl font-black tabular-nums text-emerald-800" data-testid="break-net-preview">{netH}u {String(netM).padStart(2, "0")}m</div>
          </div>
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2">
          <button onClick={onClose} className="pb-btn-outline flex-1" data-testid="btn-cancel-stop">Annuleren</button>
          <button onClick={submit} disabled={saving} className="pb-btn-primary flex-1" data-testid="btn-confirm-stop">
            {saving ? "Bezig…" : "Timer stoppen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyHour({ label, minutes, testId }) {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  return (
    <div className="border-2 border-slate-900 rounded p-2 text-center" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="pb-heading text-xl font-black tabular-nums">{h}u {String(m).padStart(2,"0")}m</div>
    </div>
  );
}
